import React, { useState, useEffect } from 'react';
import { Send, CheckCircle } from 'lucide-react';
import api, { formatApiErrorDetail } from '../lib/api';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    const handler = (e) => {
      if (e.detail && e.detail.service) {
        setFormData((prev) => ({ ...prev, service: e.detail.service }));
      }
    };
    window.addEventListener('preselect-service', handler);
    return () => window.removeEventListener('preselect-service', handler);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    try {
      await api.post('/contacts', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        service: formData.service || null,
        message: formData.message || null,
      });
      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      }, 4000);
    } catch (err) {
      setSubmitError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section id="contact" className="contact-section">
        <div className="container">
          <div className="success-message">
            <CheckCircle size={64} className="success-icon" />
            <h2 className="heading-1">Thank You!</h2>
            <p className="body-large">
              Your booking request has been received. I&apos;ll be in touch within 24 hours to confirm your session.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="contact-section">
      <div className="container">
        <div className="contact-content">
          <div className="contact-info">
            <h2 className="heading-1">Begin Your Transformation</h2>
            <p className="body-large">
              Ready to experience profound healing and transformation? Book your session today 
              and take the first step toward your highest potential.
            </p>
            
            <div className="info-items">
              <div className="info-item">
                <h3 className="heading-3">Session Options</h3>
                <p className="body-medium">In-person or virtual sessions available</p>
              </div>
              <div className="info-item">
                <h3 className="heading-3">Response Time</h3>
                <p className="body-medium">Within 24 hours on business days</p>
              </div>
              <div className="info-item">
                <h3 className="heading-3">Flexible Scheduling</h3>
                <p className="body-medium">Evening and weekend appointments available</p>
              </div>
            </div>
          </div>

          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name" className="form-label">Full Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="form-input"
                required
                placeholder="Enter your name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-input"
                required
                placeholder="your@email.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone" className="form-label">Phone Number</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="form-input"
                placeholder="+1 (555) 000-0000"
              />
            </div>

            <div className="form-group">
              <label htmlFor="service" className="form-label">Service Interest *</label>
              <select
                id="service"
                name="service"
                value={formData.service}
                onChange={handleChange}
                className="form-select"
                required
              >
                <option value="">Select a service</option>
                <option value="nlp">NLP Coaching</option>
                <option value="reiki">Reiki Healing</option>
                <option value="akashic">Akashic Records Reading</option>
                <option value="women-relationship">Relationship Workshop & Counselling for Women</option>
                <option value="men-relationship">Relationship Workshop & Counselling for Men</option>
                <option value="pre-marital">Pre-Marital Compatibility Counselling</option>
                <option value="relationship-pain">Counselling for Pain in Any Relationship</option>
                <option value="live-in">Live-In Relationship Counselling</option>
                <option value="combination">Combination Session</option>
                <option value="consultation">Free Consultation</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="message" className="form-label">Message</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                className="form-textarea"
                rows="4"
                placeholder="Tell me a bit about what you're seeking..."
              ></textarea>
            </div>

            <button 
              type="submit" 
              className="btn-primary btn-large btn-full"
              disabled={isSubmitting}
              data-testid="contact-submit-btn"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
              {!isSubmitting && <Send size={18} style={{ marginLeft: '0.5rem' }} />}
            </button>
            {submitError && <div className="auth-error" data-testid="contact-error">{submitError}</div>}
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
